import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/ops/ai/memory")({
  component: AdminAIMemoryPage,
  head: () => ({ meta: [{ title: "AI Memory — Admin" }] }),
});

type GlobalStats = {
  days: number;
  hitRatio: number | null;
  byTenant: Array<{
    tenant_id: string;
    facts: number;
    business: number;
    semantic: number;
    uses: number;
  }>;
  events: {
    hits: number;
    misses: number;
    created: number;
    updated: number;
    deleted: number;
    used: number;
  };
  topKeys: Array<{ fact_key: string; category: string; tenants: number; uses: number }>;
  categories: Array<{ category: string; count: number }>;
  storage: {
    active_facts?: number;
    open_working?: number;
    facts_bytes?: number;
    events_bytes?: number;
  };
};

function formatBytes(n?: number) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function AdminAIMemoryPage() {
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/ai/memory?days=30", { credentials: "include" });
      if (!res.ok) throw new Error(String(res.status));
      setStats((await res.json()) as GlobalStats);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <Brain className="h-5 w-5 text-violet-600" />
            AI Memory
          </h1>
          <p className="text-sm text-muted-foreground">Memoria operativa estructurada por tenant (sin vector DB).</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => void load()} className="gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : !stats ? (
        <p className="text-sm text-muted-foreground">No se pudieron cargar estadísticas.</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card title="Hit ratio" value={stats.hitRatio != null ? `${Math.round(stats.hitRatio * 100)}%` : "—"} />
            <Card title="Hechos activos" value={String(stats.storage.active_facts ?? 0)} />
            <Card title="Working abiertas" value={String(stats.storage.open_working ?? 0)} />
            <Card
              title="Storage"
              value={`${formatBytes(stats.storage.facts_bytes)} / ${formatBytes(stats.storage.events_bytes)}`}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {(["hits", "misses", "created", "updated", "deleted", "used"] as const).map((k) => (
              <Card key={k} title={k} value={String(stats.events[k] ?? 0)} />
            ))}
          </div>

          <section>
            <h2 className="mb-2 text-sm font-semibold">Top tenants</h2>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-left text-xs">
                <thead className="border-b bg-muted/40">
                  <tr>
                    <th className="px-3 py-2">Tenant</th>
                    <th className="px-3 py-2">Facts</th>
                    <th className="px-3 py-2">Business</th>
                    <th className="px-3 py-2">Semantic</th>
                    <th className="px-3 py-2">Uses</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.byTenant.map((t) => (
                    <tr key={t.tenant_id} className="border-b last:border-0">
                      <td className="px-3 py-2 font-mono">{t.tenant_id}</td>
                      <td className="px-3 py-2">{t.facts}</td>
                      <td className="px-3 py-2">{t.business}</td>
                      <td className="px-3 py-2">{t.semantic}</td>
                      <td className="px-3 py-2">{t.uses}</td>
                    </tr>
                  ))}
                  {stats.byTenant.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                        Sin memorias todavía
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div className="grid gap-6 lg:grid-cols-2">
            <section>
              <h2 className="mb-2 text-sm font-semibold">Top memorias</h2>
              <ul className="space-y-1 text-xs">
                {stats.topKeys.map((k) => (
                  <li key={`${k.category}:${k.fact_key}`} className="rounded border px-2 py-1.5">
                    <code className="font-semibold">{k.fact_key}</code>
                    <span className="text-muted-foreground">
                      {" "}
                      · {k.category} · {k.tenants} tenants · {k.uses} uses
                    </span>
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h2 className="mb-2 text-sm font-semibold">Categorías</h2>
              <ul className="space-y-1 text-xs">
                {stats.categories.map((c) => (
                  <li key={c.category} className="flex justify-between rounded border px-2 py-1.5">
                    <span>{c.category}</span>
                    <span className="font-medium">{c.count}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function Card({ title, value }: { title: string; value: string }) {
  return (
    <div className="rounded-lg border px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{title}</p>
      <p className="text-sm font-semibold">{value}</p>
    </div>
  );
}
